import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { getAssignmentQuizResultsDetailed } from "../../../services/organization/classroom/classroomAssignment.service";

function ClassroomAssignmentResult() {

  const location = useLocation();
  const navigate = useNavigate();

  const {
    assignmentId,
    quizId,
    userId,
  } = location.state || {};

  const [quizResults, setQuizResults] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    if (assignmentId && quizId && userId) {
      fetchResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, quizId, userId]);

  async function fetchResults() {
    try {
      const response = await getAssignmentQuizResultsDetailed({
        assignment_id: assignmentId,
        quiz_id: quizId,
        user_id: userId,
      });
      if (response && response.success && response.data) {
        setQuizResults(response.data);
      }
    } catch (error) {
    }
  }

  const handlePrevQuestion = () => {
    setCurrentQuestionIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextQuestion = () => {
    if (quizResults) {
      setCurrentQuestionIndex((prev) =>
        Math.min((quizResults.questions?.length || 1) - 1, prev + 1)
      );
    }
  };

  const handlePlayVideo = (videoId, timestamp, videoUrl) => {
    navigate("/user/resultvideo", {
      state: {
        videoId,
        timestamp,
        videoUrl,
      },
    });
  };

  const renderMediaContent = (type, url, content, showContentValue = false) => (
    <div className="tw-my-4 tw-text-center">
      {showContentValue && <div className="![font-weight:var(--font-weight-semibold)] tw-mb-2 tw-font-semibold">{content}</div>}
      {type === "image" && (
        <img
          src={url}
          alt={content}
          className="tw-max-w-full tw-rounded-xl"
          style={{
            maxWidth: "300px",
            maxHeight: "200px",
            objectFit: "contain",
          }}
        />
      )}
      {type === "audio" && (
        <audio
          controls
          src={url}
          className="tw-w-full"
          style={{ maxWidth: "300px" }}
        >
          Your browser does not support audio.
        </audio>
      )}
    </div>
  );

  if (!quizResults || !assignmentId || !quizId || !userId) {
    return <div className="!tw-text-[var(--secondary-color)] tw-text-center tw-py-12 tw-text-lg tw-text-slate-500">Loading...</div>;
  }

  const currentQuestion = quizResults.questions?.[currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <div className="!tw-text-[var(--secondary-color)] tw-text-center tw-py-12 tw-text-lg tw-text-slate-500">
        Question data not available
      </div>
    );
  }

  let userAnswers = [];
  if (currentQuestion.user_answer) {
    if (Array.isArray(currentQuestion.user_answer)) {
      userAnswers = currentQuestion.user_answer;
    } else if (typeof currentQuestion.user_answer === "string") {
      try {
        const parsed = JSON.parse(currentQuestion.user_answer);
        userAnswers = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        userAnswers = [currentQuestion.user_answer];
      }
    } else {
      userAnswers = [currentQuestion.user_answer];
    }
  }
  const correctOptions = currentQuestion.correct_option || [];

  // Debug logging

  return (
    <div className="tw-ml-auto tw-mr-auto tw-w-full tw-max-w-[80rem] ui-container tw-p-0 md:tw-py-6">
      <div className="tw-mb-4">
        <h3 className="tw-font-bold tw-mb-1">Classroom Assignment Quiz Results</h3>
        {quizResults.quiz && (
          <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">
            {quizResults.quiz.assignment_name} - {quizResults.quiz.name}
          </p>
        )}
      </div>

      {/* Quiz Summary Card */}
      {quizResults.summary && (
        <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card tw-mb-6"}>
          <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
            <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-items-center"}>
              <div className="tw-min-w-[0px] ui-column md:tw-w-2/3">
                <h5 className="tw-text-[1rem] tw-font-semibold tw-text-[#2c3e50] [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] tw-overflow-hidden tw-leading-[1.5rem] ui-card-title tw-mb-4">Quiz Summary</h5>
                <div className={"tw-flex tw-flex-wrap tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] [--grid-x:1rem] [--grid-y:1rem] ui-row tw-gap-grid-3"}>
                  <div className="tw-min-w-[0px] ui-column tw-w-1/2 md:tw-w-1/4">
                    <div className="tw-text-center">
                      <div className="!tw-text-[var(--primary-color)] tw-text-xl tw-font-bold tw-text-brand-600">{quizResults.summary.scorePercentage}%</div>
                      <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-sm tw-text-slate-500">Score</div>
                    </div>
                  </div>
                  <div className="tw-min-w-[0px] ui-column tw-w-1/2 md:tw-w-1/4">
                    <div className="tw-text-center">
                      <div className="!tw-text-[var(--success-color)] tw-text-xl tw-font-bold tw-text-emerald-600">{quizResults.summary.correctAnswers}</div>
                      <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-sm tw-text-slate-500">Correct</div>
                    </div>
                  </div>
                  <div className="tw-min-w-[0px] ui-column tw-w-1/2 md:tw-w-1/4">
                    <div className="tw-text-center">
                      <div className="!tw-text-[var(--info-color)] tw-text-xl tw-font-bold tw-text-sky-600">{quizResults.summary.totalQuestions}</div>
                      <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-sm tw-text-slate-500">Total</div>
                    </div>
                  </div>
                  <div className="tw-min-w-[0px] ui-column tw-w-1/2 md:tw-w-1/4">
                    <div className="tw-text-center">
                      <div className="!tw-text-[var(--warning-color)] tw-text-xl tw-font-bold tw-text-amber-600">{quizResults.summary.totalPoints}</div>
                      <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-sm tw-text-slate-500">Points</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="tw-min-w-[0px] ui-column md:tw-w-1/3 tw-text-center tw-mt-4 md:tw-mt-0">
                <div className={`[&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-font-medium ui-badge tw-text-lg tw-px-6 tw-py-2   ${quizResults.summary.passed ? "tw-bg-emerald-600" : "tw-bg-rose-600"}`}>
                  <Icon icon={quizResults.summary.passed ? 'mdi:check-circle' : 'mdi:close-circle'} className="tw-me-2" />
                  {quizResults.summary.passed ? 'Passed' : 'Failed'}
                </div>
                <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-sm tw-text-slate-500 tw-mt-2">
                  Pass Mark: {quizResults.quiz.passing_percentage}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="tw-bg-white tw-shadow-sm tw-p-6 tw-rounded-xl">
        {/* Question Header */}
        <div className="tw-mb-6">
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-center">
            <div className="md:tw-text-left tw-text-center">
              <h4 className="tw-font-bold tw-mb-1">
                Question {currentQuestionIndex + 1} of{" "}
                {quizResults.questions?.length || 0}
              </h4>
              {currentQuestion.video_timestamp && (
                <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm">
                  Timestamp: {currentQuestion.video_timestamp}
                </div>
              )}
            </div>

            {currentQuestion.ref_video_id &&
              currentQuestion.video_timestamp && (
                <div className="tw-mt-4 md:tw-mt-0">
                  <button
                    className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-primary ui-button-sm"}
                    style={{ border: "none" }}
                    onClick={() =>
                      handlePlayVideo(
                        currentQuestion.ref_video_id,
                        currentQuestion.video_timestamp,
                        currentQuestion.video_url
                      )
                    }
                  >
                    <Icon icon="mdi:play-circle" /> Play Video
                  </button>
                </div>
              )}
          </div>
        </div>

        {/* Question Text */}
        <div className="tw-mb-6 tw-text-left">
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
            <p className="![font-weight:var(--font-weight-semibold)] tw-font-semibold tw-mb-0">{currentQuestion.question}</p>
            {/* Answer Type Badge */}
            <span className={`[&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-pl-2.5 tw-pr-2.5 tw-pt-1 tw-pb-1 tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] ui-badge   ${currentQuestion.answer_type === "multiple" ? "tw-bg-sky-600" : "!tw-bg-[var(--secondary-color)] tw-bg-slate-500"}`}>
              <Icon
                icon={currentQuestion.answer_type === 'multiple' ? 'mdi:checkbox-multiple-marked' : 'mdi:radiobox-marked'}
                className="tw-me-1"
                width="14"
              />
              {currentQuestion.answer_type === 'multiple' ? 'Multiple Choice' : 'Single Choice'}
            </span>
          </div>
          {(currentQuestion.question_content_type === "image" ||
            currentQuestion.question_content_type === "audio") &&
            renderMediaContent(
              currentQuestion.question_content_type,
              currentQuestion.question_media_url,
              currentQuestion.question
            )}
        </div>

        {/* Options List */}
        <div className="tw-mb-4">
          <h6 className="!tw-text-[var(--secondary-color)] tw-text-slate-500">
            Answers
            <small className="!tw-text-[var(--secondary-color)] tw-ms-2 tw-text-slate-500">
              ({currentQuestion.answer_type === 'single'
                ? 'Select the right answer'
                : 'Select all correct answers - missing any one makes the answer wrong'})
            </small>
          </h6>
          <div className="tw-flex tw-flex-col tw-gap-4">
            {(currentQuestion.question_options || currentQuestion.options || []).map((option) => {
              // Handle both string and number comparisons for user answers
              const isUserSelected = userAnswers.includes(option.id) ||
                                     userAnswers.includes(option.id.toString()) ||
                                     userAnswers.includes(String(option.id));
              const isCorrectAnswer = correctOptions.includes(
                option.content_value
              );

              return (
                <div
                  key={option.id}
                  className={`[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-border tw-p-4 tw-rounded-xl   ${
                    isUserSelected || isCorrectAnswer ? "tw-bg-slate-50" : ""
                  }`}
                >
                  {option.content_type === "text" ? (
                    <p className="tw-mb-2">{option.content_value}</p>
                  ) : (
                    renderMediaContent(
                      option.content_type,
                      option.media_url,
                      option.content_value,
                      true
                    )
                  )}
                  <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-2">
                    {isUserSelected && (
                      <span
                        className={"[&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-pl-2.5 tw-pr-2.5 tw-pt-1 tw-pb-1 tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] ui-badge"}
                        style={{ backgroundColor: "#e0f3ff", color: "#0277bd" }}
                      >
                        Your Choice
                      </span>
                    )}
                    {isCorrectAnswer && (
                      <span
                        className={"[&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-pl-2.5 tw-pr-2.5 tw-pt-1 tw-pb-1 tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] ui-badge"}
                        style={{ backgroundColor: "#e6f7ec", color: "#2e7d32" }}
                      >
                        Correct Answer
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Feedback */}
        <div className="tw-text-center tw-mt-6">
          {currentQuestion.user_is_correct ? (
            <div className="!tw-text-[var(--success-color)] tw-text-emerald-600 tw-flex tw-justify-center tw-items-center tw-gap-2">
              <Icon icon="mdi:check-circle" />
              <span className="![font-weight:var(--font-weight-semibold)] tw-font-semibold">Correct</span>
            </div>
          ) : (
            <div className="!tw-text-[var(--danger-color)] tw-text-rose-600 tw-flex tw-justify-center tw-items-center tw-gap-2">
              <Icon icon="mdi:close-circle" />
              <span className="![font-weight:var(--font-weight-semibold)] tw-font-semibold">Incorrect</span>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mt-6 tw-pt-4 tw-border-t"}>
          <div className="tw-min-w-[0px] ui-column tw-w-full tw-flex tw-flex-col md:tw-flex-row tw-justify-center tw-gap-4 tw-mt-2">
            <button
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0}
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-secondary tw-w-full md:tw-w-auto tw-px-6"}
            >
              <Icon icon="mdi:chevron-left" /> Previous
            </button>
            <button
              onClick={handleNextQuestion}
              disabled={
                (quizResults.questions?.length || 0) === 0 ||
                currentQuestionIndex === (quizResults.questions?.length || 1) - 1
              }
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-w-full md:tw-w-auto tw-px-6"}
            >
              Next <Icon icon="mdi:chevron-right" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClassroomAssignmentResult;
